import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Decimal from 'decimal.js-light';
import { map, Observable, of } from 'rxjs';
import { AutoLog } from '../../../common/public-api';
import { CurrencyExchangeService } from '../../../core/services/currency-exchange.service';
import { isNullOrEmpty } from '../../../core/utils/common-functions';
import * as NGUtils from '../../../core/utils/ng-functions';
import { BaseComponent } from '../base.component';

export const CURRENCY_WIDGET_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CurrencyWidgetComponent),
  multi: true,
};

/**
 * 幣別元件
 *
 * @export
 * @class CurrencyWidgetComponent
 * @extends {BaseComponent}
 * @implements {OnChanges}
 * @implements {OnInit}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'sigv-currency-widget',
  templateUrl: './currency-widget.component.html',
  styleUrls: ['./currency-widget.component.scss'],
  providers: [CURRENCY_WIDGET_VALUE_ACCESSOR],
})
export class CurrencyWidgetComponent
  extends BaseComponent
  implements OnChanges, OnInit, ControlValueAccessor
{
  /**
   * 自動換匯
   */
  @Input() autoExchange = false;

  /**
   * 目標幣別
   */
  @Input() currency?: string;

  /**
   * 是否唯讀
   */
  @Input() readonly = false;

  /**
   * 是否顯示幣別
   */
  @Input() showCurrency = true;

  /**
   * 是否停用
   */
  @Input() disabled = false;

  /**
   * 空值符號
   */
  @Input() replaceNullWith = '-';

  /**
   * 換匯日期
   */
  @Input() exchangeDate?: string;

  /**
   * 幣值
   */
  @Input() amount?: number;

  /**
   * Focus 事件
   */
  @Output() focused = new EventEmitter<any>();

  /**
   * Blur 事件
   */
  @Output() blured = new EventEmitter<any>();

  /**
   * Input 事件
   */
  @Output() inputed = new EventEmitter<any>();

  @Output() exchangeCurrencyChange = new EventEmitter<string>();

  /**
   * 目標幣別設定
   */
  currencySetting?: CurrencySetting;

  /**
   * 目標幣別小數點設定
   */
  digitInfo?: { minInt: any; minFraction: any; maxFraction: any };
  model?: number;
  viewModel?: number;
  _exchangeCurrency?: string;
  onModelChange = (event: number | undefined) => {};
  onModelTouched = () => {};

  constructor(
    injector: Injector,
    private currencyExchangeService: CurrencyExchangeService,
    private cd: ChangeDetectorRef
  ) {
    super(injector);
  }

  /**
   * 目標換匯幣別
   */
  @Input() get exchangeCurrency(): string | undefined {
    return this._exchangeCurrency;
  }

  set exchangeCurrency(value: string | undefined) {
    if (isNullOrEmpty(value)) {
      this.viewModel = this.model;
    } else {
      this.doCurrencyExchange(
        this.viewModel,
        this._exchangeCurrency,
        value
      ).subscribe((value: number | undefined) => {
        this.viewModel = value;
      });
    }
    this.updateExchangeCurrency(isNullOrEmpty(value) ? this.currency : value);
    this.setCurrencySetting();
  }

  //#region Angular lifecycle hooks
  @AutoLog()
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currency'] && !changes['currency'].firstChange) {
      if (
        isNullOrEmpty(changes['currency'].previousValue) === false &&
        isNullOrEmpty(changes['currency'].currentValue) === false &&
        this.autoExchange
      ) {
        const previousCurrency = changes['currency'].previousValue as string;
        const currentCurrency = changes['currency'].currentValue as string;
        this.doCurrencyExchange(
          this.model,
          previousCurrency,
          currentCurrency
        ).subscribe((value: number | undefined) => {
          this.viewModel = value;
          this.updateModel(value);
        });
      }
      this.updateExchangeCurrency(this.currency);
      this.setCurrencySetting();
    }
  }

  ngOnInit(): void {
    if (isNullOrEmpty(this._exchangeCurrency)) {
      this.updateExchangeCurrency(this.currency);
    }
    this.setCurrencySetting();
    this.writeValue(this.amount);
  }
  //#endregion Angular lifecycle hooks

  //#region ControlValueAccessor 實作
  @AutoLog()
  writeValue(obj: any): void {
    this.model = obj;
    this.modelToViewModel();
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }
  //#endregionControlValueAccessor 實作

  isEmpty(value: any): boolean {
    return isNullOrEmpty(value);
  }

  /**
   * 畫面上的顯示值 change 事件
   *
   * @param {number} event
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  onViewModelChange(event: number) {
    this.viewModelToModel();
  }

  /**
   * 從環境設定檔取得目標幣別設定
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private setCurrencySetting(): void {
    const targetCurrency = isNullOrEmpty(this._exchangeCurrency)
      ? this.currency
      : this._exchangeCurrency;
    const currencySettings =
      globalThis.sigv.appContext.envConfig.format.currencies ?? [];
    this.currencySetting = currencySettings.find(
      (x: { currency: string | undefined }) => x.currency === targetCurrency
    );
    if (isNullOrEmpty(this.currencySetting)) {
      throw 'Currency setting is not found.';
    }
    this.digitInfo = NGUtils.extractDigitsInfo(this.currencySetting!.format);
    if (this.digitInfo.maxFraction === 0) {
      delete this.digitInfo.maxFraction;
    }
    if (this.digitInfo.minFraction === 0) {
      delete this.digitInfo.minFraction;
    }
  }

  /**
   * 更新 model 值
   *
   * @private
   * @param {number} [value]
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private updateModel(value?: number): void {
    this.model = value;
    setTimeout(() => this.onModelChange(value), 0);
  }

  /**
   * 更新換匯幣別值
   *
   * @private
   * @param {string} [value]
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private updateExchangeCurrency(value?: string): void {
    this._exchangeCurrency = value;
    setTimeout(() => {
      this.exchangeCurrencyChange.emit(this.exchangeCurrency);
    }, 0);
  }

  /**
   * 換匯
   *
   * @private
   * @param {number} [amount] 目標金額
   * @param {string} [src] 原始幣別
   * @param {string} [target] 目標幣別
   * @return {*}  {(Observable<number | undefined>)}
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private doCurrencyExchange(
    amount?: number,
    src?: string,
    target?: string
  ): Observable<number | undefined> {
    if (isNullOrEmpty(amount) || isNullOrEmpty(src) || isNullOrEmpty(target)) {
      return of(amount);
    }
    const observable = this.currencyExchangeService
      .getExchangeRate(src!, target!)
      .pipe(
        map((rate: any) => {
          const value = new Decimal(amount!).mul(rate).toNumber();
          return value;
        })
      );

    return observable;
  }

  /**
   * 畫面上金額換算成實際幣別金額
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private viewModelToModel(): void {
    this.doCurrencyExchange(
      this.viewModel,
      this._exchangeCurrency,
      this.currency
    ).subscribe((value: number | undefined) => {
      this.updateModel(value);
    });
  }

  /**
   * 實際幣別金額換算成畫面上金額
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private modelToViewModel(): void {
    this.doCurrencyExchange(
      this.model,
      this.currency,
      this._exchangeCurrency
    ).subscribe((value: number | undefined) => {
      this.viewModel = value;
    });
  }
}

export interface CurrencySetting {
  currency: string;
  format: string;
  step: number;
  round: 'off' | 'up' | 'down';
}
